"use client";

import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
  Activity,
  Bug,
  CheckSquare,
  Dumbbell,
  Microscope,
  Palette,
} from "lucide-react";

import { animateChildren } from "@/lib/animation-utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { vaccineArray } from "@/utils/items.ts";

export default function PhysicalAssessmentStep() {
  const { control, formState: { dirtyFields }, setValue, watch } = useFormContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const currentVaccinations = watch("vaccination") || [];


  const handleVaccinationChange = (value: string) => {
    if (currentVaccinations.includes(value)) {
      setValue(
        "vaccination",
        currentVaccinations.filter((v: string) => v !== value),
      );
    } else {
      setValue("vaccination", [...currentVaccinations, value]);
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      // Set initial state for children
      Array.from(containerRef.current.children).forEach((child) => {
        (child as HTMLElement).style.opacity = "0";
        (child as HTMLElement).style.transform = "translateY(20px)";
      });

      // Animate children with staggered delay
      requestAnimationFrame(() => {
        if (containerRef.current) {
          animateChildren(
            containerRef.current,
            {
              opacity: 1,
              translateY: 0,
            },
            {
              duration: 400,
              easing: "ease-out",
            },
          );
        }
      });
    }
  }, []);

  const evaluationOptions = ["Excellent", "Good", "Average", "Poor"];
  const bodyTypeOptions = ["Muscular", "Lean", "Average", "Heavy"];
  const colorOptions = ["Solid", "Spotted", "Mixed", "Patterned"];
  const developmentOptions = ["Excellent", "Good", "Average", "Poor"];
  const conditionOptions = ["Excellent", "Good", "Concerning", "Poor"];
  const structureOptions = ["Excellent", "Good", "Average", "Poor"];
  const conformationOptions = ["Excellent", "Good", "Average", "Poor"];

  return (
    <div ref={containerRef} className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-slate-900">
          Physical Assessment
        </h2>
        <p className="text-slate-500">
          Enter details about the calf&#39;s physical characteristics and health
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField
          control={control}
          name="tickCount"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Bug className="h-4 w-4" />
                Tick Count
                {dirtyFields.tickCount && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter tick count"
                  type="number"
                  {...field}
                />
              </FormControl>
              {dirtyFields.tickCount && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="headEvaluation"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Activity className="h-4 w-4" />
                Head Evaluation
                {dirtyFields.headEvaluation && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select evaluation" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {evaluationOptions.map((option) => (
                    <SelectItem
                      key={option}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(option)}
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.headEvaluation && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="bodyType"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Activity className="h-4 w-4" />
                Body Type
                {dirtyFields.bodyType && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select body type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {bodyTypeOptions.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.bodyType && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="color"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Palette className="h-4 w-4" />
                Color Pattern
                {dirtyFields.color && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select color pattern" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {colorOptions.map((option) => (
                    <SelectItem
                      key={option}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(option)}
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.color && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="muscleDevelopment"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Dumbbell className="h-4 w-4" />
                Muscle Development
                {dirtyFields.muscleDevelopment && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select development" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {developmentOptions.map((option) => (
                    <SelectItem
                      key={option}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(option)}
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.muscleDevelopment && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="umbilicalCondition"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Microscope className="h-4 w-4" />
                Umbilical Condition
                {dirtyFields.umbilicalCondition && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {conditionOptions.map((option) => (
                    <SelectItem
                      key={option}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(option)}
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.umbilicalCondition && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="bodyStructure"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Activity className="h-4 w-4" />
                Body Structure
                {dirtyFields.bodyStructure && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select structure" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {structureOptions.map((option) => (
                    <SelectItem
                      key={option}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(option)}
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.bodyStructure && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="generalConformation"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Activity className="h-4 w-4" />
                General Conformation
                {dirtyFields.generalConformation && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select conformation" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {conformationOptions.map((option) => (
                    <SelectItem
                      key={option}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(option)}
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.generalConformation && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-3 px-2">
          <FormLabel className="flex items-center gap-2 flex-wrap">
            Vaccination
            {dirtyFields.vaccination && (
              <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                Changed
              </span>
            )}
          </FormLabel>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {vaccineArray.map((vaccine) => (
              <div key={vaccine.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`vaccine-${vaccine.value}`}
                  checked={currentVaccinations.includes(vaccine.value)}
                  onCheckedChange={() => handleVaccinationChange(vaccine.value)}
                />
                <label
                  htmlFor={`vaccine-${vaccine.value}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {vaccine.label}
                </label>
              </div>
            ))}
          </div>
          {dirtyFields.vaccination && (
            <p className="text-[11px] text-amber-600 font-medium mt-1">
              Data has been changed
            </p>
          )}
        </div>

        <FormField
          control={control}
          name="earTagVerified"
          render={({ field }) => (
            <FormItem className="flex flex-col rounded-md border p-4 md:col-span-2">
              <div className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="flex items-center gap-2 flex-wrap">
                    <CheckSquare className="h-4 w-4" />
                    Ear Tag Verified
                    {dirtyFields.earTagVerified && (
                      <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                        Changed
                      </span>
                    )}
                  </FormLabel>
                  <p className="text-sm text-muted-foreground">
                    Confirm that the ear tag has been verified and matches records
                  </p>
                </div>
              </div>
              {dirtyFields.earTagVerified && (
                <p className="text-[11px] text-amber-600 font-medium mt-1 ml-9">
                  Data has been changed
                </p>
              )}
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
